import { FC, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import {
  buttonContentStyle,
  buttonStyle,
  buttonTextStyle,
  circleContainerStyle,
  containerStyle,
  leftContentStyle,
  subtitleStyle,
  textContentStyle,
  titleStyle
} from './styles.ts';

import { TonChallenge } from '@/api/ton/types.ts';
import { helpers } from '@/helpers.ts';
import { TonChallengeStates, TonChallengeTypes } from '@/enums/apiEnums.ts';
import TonController from '@/api/ton/ton.ts';
import FriendsShape from '@/components/common/svg/shapes/FriendsShape.tsx';
import HeartSimpleShape from '@/components/common/svg/shapes/HeartSimpleShape.tsx';
import SevenShape from '@/components/common/svg/shapes/SevenShape.tsx';
import LottieAnimation from '@/animations/LottieAnimation.tsx';
import loadingAnimationData from '@/components/common/svg/assets/loader_v2.json';
import { useTonStore } from '@/store/useTonStore.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import Modal from '@/animations/Modal.tsx';
import InviteFriendPopup from '@/components/EarnTon/Challenges/UnlockBonusesPopup/InviteFriendPopup.tsx';

interface ChallengeConfig {
  title: string;
  subtitle: string;
  icon: JSX.Element | null;
  onButtonClick: () => void;
}

interface ChallengeItemProps {
  challengeData: TonChallenge;
}

const ChallengeItem: FC<ChallengeItemProps> = ({ challengeData }) => {
  const { t } = useTranslation();
  const { archiveChallenge } = useTonStore();

  const [isClaiming, setIsClaiming] = useState<boolean>(false);

  const [isInviteFriendModalOpen, setIsInviteFriendModalOpen] =
    useState<boolean>(false);

  const openInviteFriendModal = () => setIsInviteFriendModalOpen(true);
  const closeInviteFriendModal = () => setIsInviteFriendModalOpen(false);

  const challengeConfig: Record<
    TonChallengeTypes,
    ChallengeConfig | undefined
  > = {
    [TonChallengeTypes.TYPE_INVITE_FRIEND]: {
      title: t('earn.challenge.invite_friend'),
      subtitle: `≈ ${helpers.convertMicrosToTON(challengeData.reward_ton_micros, 2)} TON`,
      icon: <FriendsShape width={30} height={30} fillColor="#fff" />,
      onButtonClick: () => openInviteFriendModal()
    },
    [TonChallengeTypes.TYPE_GET_MATCHES]: {
      title: t('earn.challenge.get_matched'),
      subtitle: `${
        challengeData.get_matches_body?.matched_user_ids?.length ?? 0
      }/10 ≈ ${helpers.convertMicrosToTON(challengeData.reward_ton_micros, 2)} TON`,
      icon: <HeartSimpleShape width={20} height={20} fillColor="#fff" />,
      onButtonClick: () => {}
    },
    [TonChallengeTypes.TYPE_SEVEN_DAY_ACTIVITY_STREAK]: {
      title: t('earn.challenge.seven_days_streak'),
      subtitle: `${
        challengeData.seven_day_activity_streak_body?.activity_dates?.length ??
        0
      }/7 ≈ ${helpers.convertMicrosToTON(challengeData.reward_ton_micros, 2)} TON`,
      icon: <SevenShape width={35} height={35} fillColor="#fff" />,
      onButtonClick: () => {}
    },
    [TonChallengeTypes.TYPE_UNSPECIFIED]: undefined
  };

  const config = challengeConfig[challengeData.type] || {
    title: '',
    subtitle: '',
    icon: null,
    onButtonClick: () => {}
  };

  const getButtonTranslation = (state: TonChallengeStates): string => {
    if (state === TonChallengeStates.STATE_IN_PROGRESS) {
      return challengeData.type === TonChallengeTypes.TYPE_INVITE_FRIEND
        ? t('earn.challenge.share_ref_link')
        : t('earn.challenge.in_progress');
    }
    if (state === TonChallengeStates.STATE_READY_TO_CLAIM) {
      return t('earn.challenge.claim');
    }
    if (state === TonChallengeStates.STATE_ARCHIVED) {
      return t('earn.challenge.archived');
    }
    return t('earn.challenge.locked');
  };

  const onButtonClick = async (): Promise<void> => {
    if (challengeData.state === TonChallengeStates.STATE_READY_TO_CLAIM) {
      try {
        setIsClaiming(true);

        await TonController.claimReward(challengeData.id);
      } catch (e) {
        catchErrors(e, true);
      } finally {
        archiveChallenge(challengeData.id);
        setIsClaiming(false);
      }
    } else if (challengeData.state === TonChallengeStates.STATE_IN_PROGRESS) {
      config.onButtonClick();
    }
  };

  return (
    <>
      <div style={containerStyle}>
        <div style={leftContentStyle}>
          <div style={circleContainerStyle}>{config.icon}</div>
          <div style={textContentStyle}>
            <p style={titleStyle}>{config.title}</p>
            <p style={subtitleStyle}>{config.subtitle}</p>
          </div>
        </div>
        <motion.div
          style={buttonStyle(challengeData.state)}
          onClick={onButtonClick}
        >
          <div style={buttonContentStyle}>
            {isClaiming ? (
              <LottieAnimation
                width={28}
                height={28}
                color="#fff"
                animationData={loadingAnimationData}
                shouldAnimate={true}
                loop={true}
              />
            ) : (
              <span style={buttonTextStyle}>
                {getButtonTranslation(challengeData.state)}
              </span>
            )}
          </div>
        </motion.div>
      </div>
      <Modal
        open={isInviteFriendModalOpen}
        handleClose={closeInviteFriendModal}
      >
        <InviteFriendPopup
          challengeData={challengeData}
          handleClose={closeInviteFriendModal}
        />
      </Modal>
    </>
  );
};

export default ChallengeItem;
